import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Dialog, Stack, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AntSwitch from '@/components/form-control/AntSwitch'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import toast from 'react-hot-toast'
import flowValveApi from '@/api/config/flowValveApi'
import {
  FlowSensorType,
  FlowValveType,
  InputValveOptions,
} from '@/types/hydraulic'
import { useUnits } from '@/hooks/useUnits'
import { availableValveDecoders } from './valveDecoderOptions'
import useTranslation from '@/hooks/useTranslation'
import DataGrid, { gridCellClasses } from '@/components/grid/DataGrid'
import type { GridCellCtx, GridColumn } from '@/components/grid/types'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import EmptyList from './EmptyList'
import CardsSkeleton from './CardsSkeleton'
import { useRuleCopy } from './use-rule-copy'
import { toMetricValue, type UnitConverters } from '@/components/grid/unitColumn'
import ValveCards from './ValveCards'
import InfoTip from './InfoTip'
import {
  VALVE_ERRORS,
  VALVE_STATES,
  VALVE_GROUP_IDS,
  VALVE_GROUP_LABEL_KEYS,
  VALVE_GROUP_TIP_KEYS,
  VALVE_TYPES_BY_GROUP,
  VALVE_TYPE_LABEL_KEYS,
  VALVE_TYPE_TIP_KEYS,
  defaultTypeForGroup,
  valveGroupOf,
  type ValveGroupId,
} from './constants'
import { writeSerially } from './serialWrite'
import useDraftRows, { isDraftRowId } from './use-draft-rows'
import useWaterSourceUsage from '@/features/config/hooks/watersource/useWaterSourceUsage'
import { getApiErrorMessage } from '@/helpers/notify'
import ConfirmDeleteModalUI from '@/components/common/ConfirmDeleteModalUI/ConfirmDeleteModalUI'
import DeleteDeviceDetails from './DeleteDeviceDetails'

const ROW_HEIGHT = 42
const HEADER_HEIGHT = 40
const MAX_VISIBLE_ROWS = 6

interface ValvesGridProps {
  interfaceId: number
  flowRuleId: number
  valves: FlowValveType[]
  sensors: FlowSensorType[]
  /** Metric l/m. A new valve defaults to the pipe it feeds. */
  pipeCapacity?: number
  options: InputValveOptions[]
  /**
   * `address-index` of every decoder a station already drives — they cannot
   * also drive a valve, so the picker leaves them out.
   */
  stationDecoderKeys?: Set<string>
  /** Purpose name → id for this controller, for matching a row's valve type. */
  purposeIdByName?: Map<string, number>
  /** Only a control hub has more than one controller to name. */
  showController?: boolean
  /** First load only — placeholder cards instead of the empty state. */
  loading?: boolean
  isViewOnly?: boolean
  onChanged: () => Promise<unknown> | void
  /** The panel is sized for the table, which only exists while editing. */
  onEditModeChange?: (editing: boolean) => void
  /**
   * SCRUM-1032 QC item 6 — whether there is staged work the popover would throw
   * away, so it can ask before closing rather than losing it silently.
   */
  onDirtyChange?: (dirty: boolean) => void
}

interface ValveRow {
  id: number
  active: boolean
  /**
   * SRC's flow valve type, as the stored number. The Device and Type columns
   * are two views of THIS field — neither holds a label, so a translated
   * option can never be what the save maps back from.
   */
  type: number
  /**
   * The device group while `type` is still `PENDING_VALVE_TYPE` — switching
   * Valve to Pump chooses the group, and the sub-type is the user's next
   * decision rather than something guessed for them.
   */
  typeGroup?: string
  io: string
  /** The decoder's address-index, shown under its name on the cards. */
  decoderRef: string
  errorCode: number
  flowSensor: string
  capacity: number
  priority: number
  startDelay: number
  stopDelay: number
  /** Tolgee keys, not text — see VALVE_STATES / VALVE_ERRORS. */
  stateKey: string
  errorKey: string
  errorName: string
  isCreateRow: boolean
}

/**
 * A row whose device group has been chosen but whose sub-type has not. It is
 * not a `type` the API knows: the row cannot be saved until the user picks one.
 */
const PENDING_VALVE_TYPE = -1

/** What a new valve or pump row starts with. */
const DEFAULT_VALVE_PRIORITY = 1
const DEFAULT_VALVE_DELAY_SECONDS = 5

/** The highest priority a valve or pump can be given. */
const VALVE_PRIORITY_MAX = 255

/** One shared empty set, so the memo below is not re-run by a new identity. */
const EMPTY_DECODER_KEYS: Set<string> = new Set()

const decoderRefOf = (option: InputValveOptions) =>
  `${option.address}-${option.index}`

/**
 * The picker's option value. It carries every line's text because Autocomplete
 * filters on this string, so the decoder's name, its address or its controller
 * all find the row — and it has to stay unique, since the save maps back from
 * it to the option.
 */
const ioLabel = (option: InputValveOptions) =>
  [option.name, decoderRefOf(option), (option as any).twiCustomName]
    .filter(Boolean)
    .join(' · ')

const sensorRefOf = (sensor: FlowSensorType) =>
  `${sensor.address}-${sensor.index}`

/**
 * The flow sensor picker's option value, in the same shape as the decoder's:
 * every line's text, so typing the sensor's name, its address or its
 * controller all find it — and unique, because the save maps back from it.
 *
 * The controller is always part of the VALUE even when it is not drawn, so the
 * label a saved row displays never depends on whether this interface happens
 * to be a control hub.
 */
const sensorLabel = (sensor: FlowSensorType) =>
  [sensor.io, sensorRefOf(sensor), sensor.twiCustomName]
    .filter(Boolean)
    .join(' · ')

/**
 * The rule's valves and pumps. Same grid conventions as the sensors list; the
 * no-flow sensor column is fed from the sensors above, which is why that list
 * is rendered first.
 */
const ValvesGrid = ({
  interfaceId,
  flowRuleId,
  valves,
  sensors,
  options,
  stationDecoderKeys,
  purposeIdByName,
  pipeCapacity,
  showController,
  loading,
  isViewOnly,
  onChanged,
  onEditModeChange,
  onDirtyChange,
}: ValvesGridProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()
  // Matches SensorsGrid. SRC's manual (Controller UI v230 §5.9.3.2) says a
  // passive Flow Valve "will be ignored", so Disabled is the plain word for it.
  const STATUS = useMemo(
    () => ({ on: t('commonStatusEnabled'), off: t('commonStatusDisabled') }),
    [t],
  )
  const {
    isMetric,
    convertLabelUnit,
    convertFromMetricV2,
    convertToMetricV2,
    getImperialUnitLabel,
  } = useUnits()
  // Pinned to the measurement system: useUnits returns fresh closures each
  // render, and this feeds a memoised callback.
  const units: UnitConverters = useMemo(
    () => ({ convertFromMetricV2, convertToMetricV2, getImperialUnitLabel }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMetric],
  )
  const isDark = useSelector(selectTheme) === mode_type.dark
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [saving, setSaving] = useState(false)
  const [edits, setEdits] = useState<Record<string, Partial<ValveRow>>>({})
  // Rows a save refused to write, so the cell that blocked it is marked. Filled
  // by the save rather than the moment a draft appears: every new row starts
  // without a decoder, and a red cell on a row the user is still filling in
  // reads as an error they made.
  const [invalidRowIds, setInvalidRowIds] = useState<Set<string>>(new Set())
  const { draftIds, addDraft, removeDraft, resetDrafts } = useDraftRows()
  const {
    valveUsedBy,
    ready: usageReady,
    error: usageError,
  } = useWaterSourceUsage(interfaceId)
  // Until the water sources are read, the map says nothing uses anything:
  // the delete waits for it (SCRUM-1032 review).
  const usageLoading = !usageReady && !usageError
  // The row the user asked to delete, held until they confirm it.
  const [pendingDelete, setPendingDelete] = useState<ValveRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  /**
   * Translated labels for the valve type numbers, and the maps back.
   *
   * Both directions are built from one table in one memo, so the options the
   * editor offers and the numbers a pick resolves to can never be out of step —
   * including right after a language switch.
   */
  const { groupLabels, groupLabelOf, groupIdByLabel, typeLabelOf, typeByLabel } =
    useMemo(() => {
      const groupLabel = (id: ValveGroupId) => t(VALVE_GROUP_LABEL_KEYS[id])
      const typeLabel = (type: number) => t(VALVE_TYPE_LABEL_KEYS[type] ?? '')

      const byGroupLabel = new Map<string, ValveGroupId>()
      VALVE_GROUP_IDS.forEach((id) => byGroupLabel.set(groupLabel(id), id))

      const byTypeLabel = new Map<string, number>()
      Object.keys(VALVE_TYPE_LABEL_KEYS).forEach((key) => {
        const type = Number(key)
        byTypeLabel.set(typeLabel(type), type)
      })

      return {
        groupLabels: VALVE_GROUP_IDS.map(groupLabel),
        groupLabelOf: groupLabel,
        groupIdByLabel: byGroupLabel,
        typeLabelOf: typeLabel,
        typeByLabel: byTypeLabel,
      }
    }, [t])

  const ioByLabel = useMemo(() => {
    const map = new Map<string, InputValveOptions>()
    options.forEach((option) => map.set(ioLabel(option), option))
    return map
  }, [options])

  const sensorByLabel = useMemo(() => {
    const map = new Map<string, FlowSensorType>()
    sensors.forEach((sensor) => map.set(sensorLabel(sensor), sensor))
    return map
  }, [sensors])

  const rows = useMemo<ValveRow[]>(() => {
    const mapped: ValveRow[] = valves.map((valve) => ({
      id: valve.id,
      active: Boolean(valve.active),
      type: Number(valve.type ?? 0),
      io:
        valve.io ||
        `${valve.address}-${valve.index}${valve.twiCustomName ? ` · ${valve.twiCustomName}` : ''}`,
      decoderRef: `${valve.address}-${valve.index}`,
      errorCode: Number(valve.errorCode ?? 0),
      flowSensor:
        sensors.find((sensor) => sensor.id === valve.flowSensorId) != null
          ? sensorLabel(sensors.find((sensor) => sensor.id === valve.flowSensorId)!)
          : copy.noFlowSensorNone,
      capacity: Number(valve.capacity ?? 0),
      priority: Number(valve.priority ?? 0),
      startDelay: Number(valve.startDelay ?? 0),
      stopDelay: Number(valve.stopDelay ?? 0),
      stateKey: VALVE_STATES[Number(valve.state ?? 0)]?.labelKey ?? '',
      errorKey: VALVE_ERRORS[Number(valve.errorState ?? 0)] ?? '',
      errorName: String(valve.errorName ?? ''),
      isCreateRow: false,
    }))

    if (bulkEditMode && !isViewOnly) {
      draftIds.forEach((id) =>
        mapped.push({
          id,
          active: true,
          // Normally closed: the everyday case, and the one that cannot shut
          // water off by surprise if the row is saved unchanged.
          type: 2,
          io: '',
          decoderRef: '',
          errorCode: 0,
          flowSensor: copy.noFlowSensorNone,
          // A valve usually carries the pipe it feeds, so that is the opening
          // guess rather than zero — which would block every station.
          capacity: Number(pipeCapacity ?? 0),
          // SCRUM-1032: the values a valve is normally set up with, rather
          // than zeros the user has to replace on every row. Priority 1 is the
          // first rank; the delays give the valve a few seconds to open before
          // flow is expected of it, and to close before the rule calls it shut.
          priority: DEFAULT_VALVE_PRIORITY,
          startDelay: DEFAULT_VALVE_DELAY_SECONDS,
          stopDelay: DEFAULT_VALVE_DELAY_SECONDS,
          stateKey: '',
          errorKey: '',
          errorName: '',
          isCreateRow: true,
        }),
      )
    }

    return mapped.map((row) => {
      const edit = edits[String(row.id)]
      return edit ? { ...row, ...edit } : row
    })
  }, [copy, valves, sensors, bulkEditMode, isViewOnly, edits, draftIds, pipeCapacity])

  /**
   * The row's device group. While the sub-type is pending, `type` says nothing
   * — the group is the label the user just picked.
   */
  const rowGroupOf = useCallback(
    (row: ValveRow) =>
      row.type === PENDING_VALVE_TYPE
        ? (groupIdByLabel.get(String(row.typeGroup ?? '')) ?? 'valve')
        : valveGroupOf(row.type),
    [groupIdByLabel],
  )

  const editable = bulkEditMode && !isViewOnly

  // The hosting popover widens for the table and narrows back for the cards.
  useEffect(() => {
    onEditModeChange?.(editable)
  }, [editable, onEditModeChange])

  // SCRUM-1032 QC item 6 — tell the popover when closing would lose something.
  //
  // A draft row counts even before anything is typed into it: the user asked
  // for it with "Add valve", and closing would take it away again.
  const hasStagedEdits = Object.keys(edits).length > 0 || draftIds.length > 0
  useEffect(() => {
    onDirtyChange?.(hasStagedEdits)
    // Leaving the grid leaves nothing staged behind it.
    return () => onDirtyChange?.(false)
  }, [hasStagedEdits, onDirtyChange])

  const getRowId = useCallback((row: ValveRow) => String(row.id), [])

  const cellClassFor = useCallback(
    (field: keyof ValveRow) =>
      ({ row }: GridCellCtx<ValveRow>) => {
        if (!bulkEditMode) return ''
        return edits[String(row.id)]?.[field] !== undefined
          ? gridCellClasses.changed
          : gridCellClasses.editable
      },
    [bulkEditMode, edits],
  )

  // SCRUM-1032 QC item 5 — a select column also carries the chevron that says
  // it picks from a list, drawn at rest rather than only once focused.
  const selectCellClassFor = useCallback(
    (field: keyof ValveRow) =>
      (ctx: GridCellCtx<ValveRow>) => [
        cellClassFor(field)(ctx),
        bulkEditMode ? gridCellClasses.dropdown : '',
      ],
    [cellClassFor, bulkEditMode],
  )

  /**
   * Three lines per option, matching the sensors picker and the station form:
   * the decoder's name, its address-index, then the controller — the last only
   * on a control hub, where there is more than one controller to tell apart.
   */
  const renderIoOption = useCallback(
    (label: string) => {
      const option = ioByLabel.get(label)
      if (!option) return label
      return (
        <Stack sx={{ gap: '2px', py: '2px' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {option.name || decoderRefOf(option)}
          </Box>
          {option.name ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {decoderRefOf(option)}
            </Box>
          ) : null}
          {showController && (option as any).twiCustomName ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {(option as any).twiCustomName}
            </Box>
          ) : null}
        </Stack>
      )
    },
    [ioByLabel, showController],
  )

  /** Matches the decoder picker: name, address-index, then controller. */
  const renderSensorOption = useCallback(
    (label: string) => {
      if (label === copy.noFlowSensorNone) {
        return (
          <Box component="span" sx={{ color: 'grey.600', fontStyle: 'italic' }}>
            {label}
          </Box>
        )
      }
      const sensor = sensorByLabel.get(label)
      if (!sensor) return label
      return (
        <Stack sx={{ gap: '2px', py: '2px' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {sensor.io || sensorRefOf(sensor)}
          </Box>
          {sensor.io ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {sensorRefOf(sensor)}
            </Box>
          ) : null}
          {showController && sensor.twiCustomName ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {sensor.twiCustomName}
            </Box>
          ) : null}
        </Stack>
      )
    },
    [copy, sensorByLabel, showController],
  )

  const toggleActive = useCallback((rowId: number, next: boolean) => {
    const key = String(rowId)
    setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), active: next } }))
  }, [])

  const handleDelete = useCallback(
    async (id: number) => {
      if (isViewOnly) return
      if (!usageReady) {
        toast.error(getApiErrorMessage(usageError, t('somethingWentWrong')))
        return
      }
      // A water source points at the valve or pump it opens through, so a
      // valve one of them uses never reaches the delete endpoint.
      const usedBy = valveUsedBy.get(Number(id))
      if (usedBy?.length) {
        toast.error(
          t('flowValveUsedByWaterSource', { names: usedBy.join(', ') }),
        )
        return
      }
      try {
        await flowValveApi.delete(String(interfaceId), String(id))
        await onChanged()
      } catch (error: any) {
        toast.error(getApiErrorMessage(error, t('commonDeleteFailed')))
      }
    },
    [t, interfaceId, isViewOnly, onChanged, valveUsedBy, usageReady, usageError],
  )

  const columns = useMemo<GridColumn<ValveRow>[]>(() => {
    const base: GridColumn<ValveRow>[] = [
      {
        field: 'active',
        headerName: t('commonLabelsStatus'),
        width: 126,
        editable: false,
        cellClass: cellClassFor('active'),
        cellRenderer: ({ row }) => (
          <Stack
            direction="row"
            data-no-cell-edit
            sx={{ alignItems: 'center', gap: '8px' }}
          >
            <AntSwitch
              width={34}
              height={18}
              widthThumb={14}
              heightThumb={14}
              borderThumb={14}
              borderTrack={9}
              transform="16"
              checked={Boolean(row.active)}
              disabled={!editable}
              // AntSwitch's disabled state is a white thumb on a white track;
              // read-only still has to show which way it is set.
              sx={{
                '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                  opacity: 1,
                  backgroundColor: '#cddada',
                },
                '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track':
                  {
                    opacity: 1,
                    backgroundColor: isDark ? '#5bcf86' : '#004744',
                  },
                '& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb': {
                  opacity: 1,
                  backgroundColor: '#fff',
                },
              }}
              onChange={(event) => toggleActive(row.id, event.target.checked)}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: row.active ? 'text.main' : 'grey.600',
              }}
            >
              {row.active ? STATUS.on : STATUS.off}
            </Typography>
          </Stack>
        ),
      },
      // Type is split in two purely for the UI: what the hardware is, then how
      // it behaves. Both columns read and write the row's single `type` number
      // through valueGetter/valueSetter, so the labels below are display only.
      {
        field: 'typeGroup',
        // "Device" for what the hardware is; "Type" below stays on the column
        // that IS SRC's `type` field, so the manual and the UI use one word for
        // one thing.
        headerName: t('flowValveColumnDevice'),
        headerTooltip: copy.tips.valveGroup,
        width: 92,
        editable,
        editor: 'select',
        editorParams: { values: groupLabels },
        valueGetter: (row) =>
          row.type === PENDING_VALVE_TYPE
            ? String(row.typeGroup ?? '')
            : groupLabelOf(valveGroupOf(row.type)),
        // Changing the device changes the type with it: no type belongs to
        // both groups, so keeping the old number would show an impossible pair.
        valueSetter: (_row, value) => ({
          type: defaultTypeForGroup(groupIdByLabel.get(String(value)) ?? 'valve'),
        }),
        cellClass: selectCellClassFor('type'),
        cellRenderer: ({ row, value }) => (
          <Tooltip
            title={t(VALVE_GROUP_TIP_KEYS[rowGroupOf(row)])}
            placement="top"
          >
            <span>{String(value ?? '')}</span>
          </Tooltip>
        ),
      },
      {
        field: 'subType',
        headerName: t('commonLabelsType'),
        headerTooltip: copy.tips.valveSubType,
        width: 140,
        editable,
        editor: 'select',
        // Only the types belonging to the row's group.
        editorParams: ({ row }: GridCellCtx<ValveRow>) => ({
          values: VALVE_TYPES_BY_GROUP[rowGroupOf(row)].map(typeLabelOf),
        }),
        valueGetter: (row) =>
          row.type === PENDING_VALVE_TYPE ? '' : typeLabelOf(row.type),
        // An unrecognised label keeps the row's current type rather than
        // guessing at one. The old code fell back to the group's first entry,
        // which is how picking "Booster pump" could be saved as "Normally open".
        valueSetter: (row, value) => ({
          type: typeByLabel.get(String(value)) ?? row.type,
        }),
        cellClass: selectCellClassFor('type'),
        cellRenderer: ({ row, value }) =>
          row.type === PENDING_VALVE_TYPE ? (
            <Box component="span" sx={{ color: 'grey.600', fontStyle: 'italic' }}>
              {copy.pickValveType}
            </Box>
          ) : (
            <Tooltip title={t(VALVE_TYPE_TIP_KEYS[row.type] ?? '')} placement="top">
              <span>{String(value ?? '')}</span>
            </Tooltip>
          ),
      },
      {
        field: 'io',
        headerName: copy.io,
        // The only flexible column. The grid shares spare width across every
        // flex column without discounting the ones already clamped at their
        // minWidth, so two flex columns overshot the panel by ~80px.
        flex: 1,
        minWidth: 150,
        editable,
        editor: 'select',
        // Per row, because what a row may be wired to depends on its own
        // valve type — and on the decoders other rows and stations have taken.
        editorParams: ({ row }: { row: ValveRow }) => ({
          values: availableValveDecoders({
            options,
            valveType: row.type,
            stationDecoderKeys: stationDecoderKeys ?? EMPTY_DECODER_KEYS,
            purposeIdByName,
            currentKey: row.decoderRef || undefined,
          }).map(ioLabel),
          renderOption: renderIoOption,
          // Three lines per option need more room than the column has.
          minWidth: 320,
        }),
        cellClass: (ctx: GridCellCtx<ValveRow>) => {
          const marks = [...selectCellClassFor('io')(ctx)]
          // Declared last so the refusal wins over the editable ring.
          if (invalidRowIds.has(String(ctx.row.id))) {
            marks.push(gridCellClasses.invalid)
          }
          return marks.filter(Boolean)
        },
        cellRenderer: ({ row, value }) =>
          value ? (
            <>{value as string}</>
          ) : (
            <Box component="span" sx={{ color: 'grey.600', fontStyle: 'italic' }}>
              {row.isCreateRow ? copy.pickOutput : ''}
            </Box>
          ),
      },
      // No State column: it is reported by the controller rather than set
      // here, and this table only exists in bulk edit. The cards carry it.
      {
        field: 'flowSensor',
        headerName: copy.noFlowSensor,
        width: 150,
        editable,
        editor: 'select',
        editorParams: {
          values: [copy.noFlowSensorNone, ...sensors.map(sensorLabel)],
          renderOption: renderSensorOption,
          minWidth: 320,
        },
        cellClass: selectCellClassFor('flowSensor'),
        headerTooltip: copy.tips.noFlowSensor,
      },
      {
        field: 'capacity',
        headerName: `${t('formsLabelsCapacity')} (${convertLabelUnit('l/m')})`,
        headerTooltip: copy.tips.capacity,
        // Fits "Capacity (l/m)" — at 104 the unit was cut to "Capacity (...".
        width: 130,
        editable,
        editor: 'number',
        // Right-aligned so the figure does not shift when the editor opens.
        numeric: true,
        cellClass: cellClassFor('capacity'),
        cellRenderer: ({ row, value }) => (
          <>
            {row.isCreateRow && !value
              ? ''
              : String(convertFromMetricV2(Number(value ?? 0), 'l/m', 'gpm', 2))}
          </>
        ),
      },
      {
        field: 'priority',
        headerName: t('formsLabelsPriority'),
        headerTooltip: copy.tips.priority,
        width: 86,
        editable,
        editor: 'number',
        // SCRUM-1079: 255 is the ceiling. The editor refuses anything past it
        // rather than clamping, and a value stored above it shows red.
        editorParams: {
          min: 0,
          max: VALVE_PRIORITY_MAX,
          outOfRangeMessage: t('formsValidationPriorityRange'),
        },
        numeric: true,
        cellClass: (ctx) => {
          const priority = Number(ctx.value)
          const outOfRange =
            ctx.value != null &&
            ctx.value !== '' &&
            (priority < 0 || priority > VALVE_PRIORITY_MAX)
          return [
            cellClassFor('priority')(ctx),
            outOfRange ? gridCellClasses.invalid : '',
          ]
        },
      },
      // The delays are secondary (they shift when the hardware moves, not what
      // it does) and the error is empty almost always — so outside bulk edit
      // they fold: one Delays column, and the error rides with the state.
      ...(editable
        ? ([
            {
              field: 'startDelay',
              headerName: copy.startDelayColumn,
              headerTooltip: copy.tips.startDelaySeconds,
              width: 120,
              editable,
              editor: 'number',
              numeric: true,
              cellClass: cellClassFor('startDelay'),
            },
            {
              field: 'stopDelay',
              headerName: copy.stopDelayColumn,
              headerTooltip: copy.tips.stopDelaySeconds,
              width: 120,
              editable,
              editor: 'number',
              numeric: true,
              cellClass: cellClassFor('stopDelay'),
            },
          ] as GridColumn<ValveRow>[])
        : ([
            {
              field: 'delays',
              headerName: copy.delays,
              headerTooltip: copy.tips.delays,
              width: 110,
              valueGetter: (row: ValveRow) =>
                row.isCreateRow
                  ? ''
                  : `${Number(row.startDelay ?? 0)}s / ${Number(row.stopDelay ?? 0)}s`,
            },
          ] as GridColumn<ValveRow>[])),
    ]

    if (!editable) return base

    return [
      ...base,
      {
        field: '__actions',
        headerName: '',
        // Sized to the icon now that the word DELETE has gone.
        width: 62,
        pinned: 'right',
        sortable: false,
        resizable: false,
        cellRenderer: ({ row }) => (
          <Box data-no-cell-edit>
            <DeleteIconButton
              isDark={isDark}
              title={row.isCreateRow ? 'Discard' : t('actionDelete')}
              onClick={() => {
                // A draft has never been written, so it is dropped locally
                // rather than going anywhere near the delete endpoint.
                if (row.isCreateRow) {
                  removeDraft(row.id)
                  setEdits((prev) => {
                    const next = { ...prev }
                    delete next[String(row.id)]
                    return next
                  })
                  setInvalidRowIds((prev) => {
                    if (!prev.has(String(row.id))) return prev
                    const remaining = new Set(prev)
                    remaining.delete(String(row.id))
                    return remaining
                  })
                  return
                }
                // A valve or pump a water source opens through can never
                // be deleted, so it is refused here rather than after a
                // confirmation the user cannot act on.
                const usedBy = valveUsedBy.get(Number(row.id))
                if (usedBy?.length) {
                  toast.error(
                    t('flowValveUsedByWaterSource', {
                      names: usedBy.join(', '),
                    }),
                  )
                  return
                }
                setPendingDelete(row)
              }}
            />
          </Box>
        ),
      },
    ]
  }, [
    copy,
    invalidRowIds,
    groupLabels,
    groupLabelOf,
    groupIdByLabel,
    typeLabelOf,
    typeByLabel,
    editable,
    options,
    sensors,
    STATUS,
    t,
    convertLabelUnit,
    convertFromMetricV2,
    cellClassFor,
    valveUsedBy,
    isDark,
    renderIoOption,
    renderSensorOption,
    toggleActive,
    removeDraft,
  ])

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await handleDelete(pendingDelete.id)
    } finally {
      setDeleting(false)
      setPendingDelete(null)
    }
  }, [handleDelete, pendingDelete])

  const handleCellValueChanged = useCallback(
    (event: { row: ValveRow; field: string; newValue: unknown }) => {
      const key = String(event.row.id)
      const patch =
        event.field === 'active'
          ? { active: event.newValue === STATUS.on }
          : // Both device columns write the row's `type`, not a field of their
            // own name. Staging them raw left `type` untouched, so switching
            // Valve to Pump changed nothing at all — and neither did picking a
            // sub-type.
            event.field === 'typeGroup'
            ? {
                typeGroup: String(event.newValue ?? ''),
                // Cleared rather than defaulted: "Pump" does not say which
                // pump, and guessing one is how a booster ends up saved as a
                // plain pump.
                type: PENDING_VALVE_TYPE,
              }
            : event.field === 'subType'
              ? {
                  type: typeByLabel.get(String(event.newValue)) ?? event.row.type,
                  typeGroup: undefined,
                }
              : event.field === 'capacity'
            ? // Capacity is shown and typed in the user's units but stored in
              // l/m. Converting here means `edits` always holds metric, so a
              // row edited on some OTHER field cannot have its untouched
              // capacity converted a second time on save.
              {
                capacity: Number(
                  toMetricValue(event.row.capacity, event.newValue, 'l/m', units),
                ),
              }
              : { [event.field]: event.newValue }

      setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), ...patch } }))
      // The complaint goes as soon as the row answers it.
      if (event.field === 'io' && event.newValue) {
        setInvalidRowIds((prev) => {
          if (!prev.has(key)) return prev
          const remaining = new Set(prev)
          remaining.delete(key)
          return remaining
        })
      }
      return false as const
    },
    [STATUS, units, typeByLabel],
  )

  const handleSave = useCallback(async () => {
    // A new row without a decoder is not a valve — there is no output to
    // drive. It used to be dropped on the way to the API, so Save looked like
    // it had worked and the row simply vanished; now it blocks the save and
    // says why. Only draft rows are checked: a saved valve always carries its
    // address-index, whose label does not have to match a picker option.
    const missingDecoder = rows.filter(
      (row) => row.isCreateRow && !ioByLabel.get(row.io),
    )
    if (missingDecoder.length > 0) {
      setInvalidRowIds(new Set(missingDecoder.map((row) => String(row.id))))
      toast.error(copy.pickDecoderFirst)
      return
    }
    setInvalidRowIds((prev) => (prev.size === 0 ? prev : new Set()))

    const entries = Object.entries(edits)
    if (entries.length === 0) {
      setBulkEditMode(false)
      return
    }
    setSaving(true)

    // A row whose device was switched but whose sub-type was never chosen has
    // no type to write. Saving it would either invent one or send -1.
    const pendingType = entries.filter(([id, edit]) => {
      const row = rows.find((entry) => String(entry.id) === id)
      return Number(edit.type ?? row?.type) === PENDING_VALVE_TYPE
    })
    if (pendingType.length > 0) {
      toast.error(copy.pickValveTypeFirst)
      setSaving(false)
      return
    }

    const payloads = entries
      .map(([id, edit]) => {
        const row = rows.find((entry) => String(entry.id) === id)
        const existing = valves.find((valve) => String(valve.id) === id)
        const io = ioByLabel.get(String(edit.io ?? row?.io ?? ''))
        // Unreachable — the guard above stops the save while any draft still
        // lacks a decoder. Kept so a draft can never reach the API without an
        // output even if that check is ever moved.
        if (isDraftRowId(Number(id)) && !io) return null
        const sensorLabelValue = String(edit.flowSensor ?? row?.flowSensor ?? '')
        const sensor = sensorByLabel.get(sensorLabelValue)
        // Only "None" clears the sensor. An unresolved label used to write 0,
        // which silently removed the valve's dry-run protection — the manual is
        // explicit that a no-flow with a sensor assigned is what stops the pump
        // (Controller UI v230 §5.9.2.3), so losing it by accident matters.
        const flowSensorId =
          sensorLabelValue === copy.noFlowSensorNone
            ? 0
            : (sensor?.id ?? Number(existing?.flowSensorId ?? 0))
        return {
          id: isDraftRowId(Number(id)) ? undefined : Number(id),
          interfaceId,
          flowRuleId,
          active: edit.active ?? row?.active ?? existing?.active ?? true,
          // Both UI columns write this number directly, so there is nothing to
          // resolve here and no label to mis-resolve.
          type: Number(edit.type ?? row?.type ?? existing?.type ?? 2),
          priority: Number(edit.priority ?? row?.priority ?? existing?.priority ?? 0),
          decoderId: io ? io.id || undefined : (existing as any)?.decoderId || undefined,
          address: io ? io.address : Number(existing?.address ?? 0),
          index: io ? io.index : Number(existing?.index ?? 0),
          flowSensorId,
          startDelay: Number(edit.startDelay ?? row?.startDelay ?? existing?.startDelay ?? 0),
          stopDelay: Number(edit.stopDelay ?? row?.stopDelay ?? existing?.stopDelay ?? 0),
          // Already metric — staged edits are converted as they are made, and
          // an untouched row still holds what the API sent.
          capacity: Number(edit.capacity ?? row?.capacity ?? existing?.capacity ?? 0),
          twiId: io?.twiId ?? existing?.twiId ?? null,
        }
      })
      .filter(Boolean) as any[]

    const { failed, lastError } = await writeSerially(payloads, (payload) =>
      payload.id ? flowValveApi.update(payload) : flowValveApi.create(payload),
    )

    setSaving(false)
    if (failed.length > 0) {
      toast.error(
        getApiErrorMessage(
          lastError,
          t('bulkSavePartialError', {
            failed: failed.length,
            total: payloads.length,
          }),
        ),
      )
      return
    }
    setEdits({})
    resetDrafts()
    setInvalidRowIds(new Set())
    setBulkEditMode(false)
    await onChanged()
  }, [
    t,
    copy,
    edits,
    flowRuleId,
    interfaceId,
    ioByLabel,
    onChanged,
    rows,
    sensorByLabel,
    valves,
    resetDrafts,
  ])

  // Above this the list scrolls internally instead of growing the row.
  const tall = rows.length > MAX_VISIBLE_ROWS

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'border.main',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: '8px',
          px: '12px',
          py: '9px',
          borderBottom: '1px solid',
          borderColor: 'border.main',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'grey.800',
          }}
        >
          {copy.valves}
          <Box component="span" sx={{ ml: '6px', fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'grey.600' }}>
            {valves.length}
          </Box>
        </Typography>
        <InfoTip text={copy.tips.valves} />
        <Box sx={{ flex: 1 }} />
        {!isViewOnly &&
          (bulkEditMode ? (
            <>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setEdits({})
                  resetDrafts()
                  setInvalidRowIds(new Set())
                  setBulkEditMode(false)
                }}
              >
                {t('commonActionsCancel')}
              </Button>
              <Button variant="primary" size="small" disabled={saving} onClick={handleSave}>
                {t('commonActionsSave')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="small" onClick={() => setBulkEditMode(true)}>
              {t('commonActionsBulkEdit')}
            </Button>
          ))}
      </Stack>

      {loading && rows.length === 0 ? (
        <CardsSkeleton isDark={isDark} />
      ) : rows.length === 0 ? (
        <EmptyList
          glyph="valve"
          title={copy.emptyValveTitle}
          hint={copy.emptyValveHint}
          actionLabel={copy.emptyValveAction}
          disabled={isViewOnly}
          onAction={() => {
            setBulkEditMode(true)
            addDraft()
          }}
        />
      ) : !editable ? (
        // Reading and editing want different shapes. Eleven columns is right
        // for editing and wrong for scanning, so the view is cards.
        <ValveCards
          rows={rows}
          isDark={isDark}
          pipeCapacity={pipeCapacity}
          formatCapacity={(value) =>
            String(convertFromMetricV2(Number(value ?? 0), 'l/m', 'gpm', 2))
          }
          capacityUnit={convertLabelUnit('l/m')}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            ...(tall
              ? { height: `${MAX_VISIBLE_ROWS * ROW_HEIGHT + HEADER_HEIGHT}px` }
              : {}),
            '& > [role="grid"]': {
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
            },
          }}
        >
          <DataGrid<ValveRow>
            columns={columns}
            rows={rows}
            getRowId={getRowId}
            autoHeight={!tall}
            rowHeight={ROW_HEIGHT}
            headerHeight={HEADER_HEIGHT}
            editMode={editable ? 'singleClick' : 'none'}
            onCellValueChanged={handleCellValueChanged}
          />
        </Box>
      )}

      {/* Sits under the list, where the row it adds will appear — rather than
          up in the header beside Cancel and Save, which are about the batch as
          a whole. */}
      {editable && rows.length > 0 && (
        <Stack
          direction="row"
          sx={{
            borderTop: '1px solid',
            borderColor: 'border.main',
            px: '8px',
            py: '6px',
          }}
        >
          <Button
            variant="secondary"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => addDraft()}
          >
            Add valve
          </Button>
        </Stack>
      )}

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => !deleting && setPendingDelete(null)}
      >
        <ConfirmDeleteModalUI
          title={t('DeleteFlowValve')}
          name={pendingDelete?.io}
          text={t('flowValveDeleteConfirmation')}
          isLoading={deleting || usageLoading}
          onClose={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
        >
          <DeleteDeviceDetails
            details={[
              { label: copy.io, value: pendingDelete?.decoderRef },
              {
                label: t('commonLabelsType'),
                value:
                  pendingDelete && pendingDelete.type !== PENDING_VALVE_TYPE
                    ? typeLabelOf(pendingDelete.type)
                    : '',
              },
              { label: copy.noFlowSensor, value: pendingDelete?.flowSensor },
              {
                label: t('commonLabelsStatus'),
                value: pendingDelete?.active
                  ? copy.active
                  : t('commonStatusDisabled'),
              },
            ]}
          />
        </ConfirmDeleteModalUI>
      </Dialog>
    </Box>
  )
}

export default ValvesGrid
